import { BASE_URL } from "../../../../global"
import type {
  AiRecommendation,
  AiSubject,
  RecommendationApiBasket,
  RecommendationApiBasketItem,
  RecommendationApiData,
} from "./aiLessonPlanningTypes"

export const RECOMMENDATIONS_LIMIT = 50

const BASKET_ORDER = ["progress", "preference", "relearn"]

const BASKET_LABEL: Record<string, string> = {
  progress: "Progress",
  preference: "Preference",
  relearn: "Relearn",
  performance: "Performance",
}

export function buildRecommendationsUrl(
  externalLearnerId: string,
  subject: AiSubject,
  limit: number
): string {
  const base = (BASE_URL || "").trim()
  if (!base) {
    throw new Error("REACT_APP_BASE_URL is not configured")
  }
  const normalisedBase = base.endsWith("/") ? base : `${base}/`
  const url = new URL("activity/recommendations", normalisedBase)
  url.searchParams.set("externalLearnerId", externalLearnerId)
  url.searchParams.set("subject", subject)
  url.searchParams.set("limit", String(limit))
  return url.toString()
}

function tutorBearerToken(): string | null {
  const stored = localStorage.getItem("authUser")
  if (!stored) return null
  try {
    const parsed = JSON.parse(stored)
    return typeof parsed === "string" && parsed.trim() !== "" ? parsed : null
  } catch {
    return null
  }
}

export async function fetchRecommendations(
  externalLearnerId: string,
  subject: AiSubject,
  signal?: AbortSignal,
  limit: number = RECOMMENDATIONS_LIMIT
): Promise<RecommendationApiData> {
  const headers: Record<string, string> = { Accept: "application/json" }
  const token = tutorBearerToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    buildRecommendationsUrl(externalLearnerId, subject, limit),
    { method: "GET", headers, signal }
  )

  let body: any = null
  try {
    body = await response.json()
  } catch (error) {
    body = null
  }

  if (!response.ok || !body || body.success === false) {
    const message =
      body?.message ||
      (response.ok ? "Unexpected response" : `Request failed (${response.status})`)
    throw new Error(message)
  }

  return (body.data ?? body) as RecommendationApiData
}

function normaliseContentType(item: RecommendationApiBasketItem): string {
  const raw = (item.contentType || item.type || "").trim().toLowerCase()
  if (raw === "quiz" || raw === "online") return "ONLINE"
  if (raw === "document" || raw === "worksheet" || raw === "pdf") return "WORKSHEET"
  return (item.contentType || item.type || "").toUpperCase()
}

function itemYear(item: RecommendationApiBasketItem): number | null {
  if (typeof item.year === "number" && Number.isFinite(item.year)) return item.year
  const levels = Array.isArray(item.levels) ? item.levels : []
  const first = levels.find(level => typeof level === "number" && Number.isFinite(level))
  return first ?? null
}

function basketSections(data: RecommendationApiData): Array<{
  reason: string
  label: string
  items: RecommendationApiBasketItem[]
}> {
  if (Array.isArray(data.baskets) && data.baskets.length > 0) {
    const byKey = new Map<string, RecommendationApiBasket>()
    data.baskets.forEach(basket => {
      if (basket?.key) byKey.set(String(basket.key).toLowerCase(), basket)
    })

    const orderedKeys = [
      ...BASKET_ORDER.filter(key => byKey.has(key)),
      ...Array.from(byKey.keys()).filter(key => BASKET_ORDER.indexOf(key) < 0),
    ]

    return orderedKeys.map(key => {
      const basket = byKey.get(key)!
      return {
        reason: key,
        label: basket.label || BASKET_LABEL[key] || key,
        items: Array.isArray(basket.items) ? basket.items : [],
      }
    })
  }

  if (Array.isArray(data.groups) && data.groups.length > 0) {
    return data.groups.map(group => ({
      reason: String(group.reason || "").toLowerCase(),
      label: group.reasonLabel || BASKET_LABEL[group.reason] || group.reason,
      items: Array.isArray(group.items) ? group.items : [],
    }))
  }

  return [
    {
      reason: "progress",
      label: "Progress",
      items: Array.isArray(data.items) ? data.items : [],
    },
  ]
}

export function toAiRecommendations(
  subject: AiSubject,
  data: RecommendationApiData
): AiRecommendation[] {
  const sections = basketSections(data)
  const mapped: AiRecommendation[] = []
  let rank = 1

  sections.forEach(section => {
    section.items.forEach(item => {
      if (!item?.sourceId || !item?.title) return
      mapped.push({
        id: `${subject}:${item.sourceId}`,
        subject,
        rank: typeof item.rank === "number" && item.rank > 0 ? item.rank : rank,
        sourceId: item.sourceId,
        title: item.title,
        contentType: normaliseContentType(item),
        domain: item.domain ?? null,
        strand: item.strand ?? null,
        year: itemYear(item),
        reason: section.reason || null,
        reasonLabel: section.label || null,
        reasonNotes: Array.isArray(item.reasons)
          ? item.reasons.filter(Boolean)
          : [],
      })
      rank += 1
    })
  })

  return mapped.sort((a, b) => a.rank - b.rank)
}
